/**
 * useProjectBudget
 *
 * Fetches EBProjects budget data for a given Scheduler project ID.
 * Caches results per projectId to avoid redundant API calls within
 * the same session.  Used by ResourceRequestPlanDrawer and
 * ResourceRequestsTab to surface budget context during allocation.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { projectsApi, EBProjectsBudgetComposite } from '@/services/api';

interface UseProjectBudgetResult {
  budgetData: EBProjectsBudgetComposite | null;
  budgetLoading: boolean;
  budgetError: string | null;
  roleBudgets: EBProjectsBudgetComposite['roleBudgets'];
}

const budgetCache = new Map<string, EBProjectsBudgetComposite>();

export function useProjectBudget(
  projectId: string | number | null | undefined,
): UseProjectBudgetResult {
  const [budgetData, setBudgetData] = useState<EBProjectsBudgetComposite | null>(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [budgetError, setBudgetError] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    if (!projectId) {
      setBudgetData(null);
      setBudgetLoading(false);
      setBudgetError(null);
      return;
    }

    const key = String(projectId);
    const cached = budgetCache.get(key);
    if (cached) {
      setBudgetData(cached);
      setBudgetLoading(false);
      setBudgetError(null);
      return;
    }

    setBudgetLoading(true);
    setBudgetError(null);

    projectsApi
      .getBudgetsFromEBProjects(key)
      .then((res) => {
        if (cancelledRef.current) return;
        budgetCache.set(key, res);
        setBudgetData(res);
      })
      .catch((err) => {
        if (cancelledRef.current) return;
        setBudgetData(null);
        setBudgetError(err instanceof Error ? err.message : 'Failed to fetch budget');
      })
      .finally(() => {
        if (!cancelledRef.current) setBudgetLoading(false);
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [projectId]);

  return {
    budgetData,
    budgetLoading,
    budgetError,
    roleBudgets: budgetData?.roleBudgets ?? [],
  };
}

/**
 * Find the role budget entry matching a given role name (case-insensitive).
 */
export function findRoleBudget(
  roleBudgets: EBProjectsBudgetComposite['roleBudgets'],
  roleName: string | undefined,
) {
  if (!roleName || !roleBudgets.length) return undefined;
  const lower = roleName.toLowerCase();
  return roleBudgets.find((rb) => rb.roleName?.toLowerCase() === lower);
}

/**
 * Safely parse the monthlyHours JSON string from a role budget entry.
 * Returns an empty record on parse failure.
 */
export function parseMonthlyHours(
  monthlyHoursJson: string | undefined,
): Record<string, number> {
  if (!monthlyHoursJson) return {};
  try {
    return JSON.parse(monthlyHoursJson) as Record<string, number>;
  } catch {
    return {};
  }
}

/**
 * Returns the EDS-compatible accent colour for a given budget usage percentage.
 * < 70% → teal (on track), 70-90% → warning (at risk), > 90% → destructive (over budget)
 */
export function budgetHealthAccent(percentUsed: number): 'teal' | 'warning' | 'destructive' {
  if (percentUsed < 70) return 'teal';
  if (percentUsed <= 90) return 'warning';
  return 'destructive';
}
